import { useMemo } from 'react';
import { Offcanvas, Tab, Tabs } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import { getObjectRecord, getObjectDelailsRaw } from './keysUtils';
import ProductsServicesKeysTab from './tabs/ProductsServicesKeysTab';
import SpecificationKeysTab from './tabs/SpecificationKeysTab';
import CustomerKeysTab from './tabs/CustomerKeysTab';
import ClientKeysTab from './tabs/ClientKeysTab';
import GeneralKeysTab from './tabs/GeneralKeysTab';
import DocumentKeysTab from './tabs/DocumentKeysTab';

type TemplateKeysSource = 'proposal' | 'contract' | 'invoice' | 'document';

interface TemplateKeysOffcanvasProps {
    show: boolean;
    source: TemplateKeysSource;
    translationNs?: 'Proposal' | 'Contract' | 'Invoice' | 'Document';
    objectData?: unknown;
    onHide: () => void;
    /** When set, clicking a key selects it (insert mode) instead of copying. */
    onKeySelect?: (key: string) => void;
}

const TemplateKeysOffcanvas = ({
    show,
    source: _source,
    translationNs: _translationNs,
    objectData,
    onHide,
    onKeySelect,
}: TemplateKeysOffcanvasProps) => {
    const { t } = useTranslation(['TemplateValueKeys', 'Objects']);

    const objectRecord = useMemo(() => getObjectRecord(objectData), [objectData]);
    const objectDelails = getObjectDelailsRaw(objectRecord);
    const showSpecification = Boolean(objectRecord);
    const showGeneral = Boolean(objectRecord);

    return (
        <Offcanvas
            show={show}
            onHide={onHide}
            placement="end"
            backdrop
            backdropClassName="template-keys-offcanvas-backdrop"
            className="off-import template-keys-offcanvas"
        >
            <Offcanvas.Header className="border-bottom" closeButton>
                <Offcanvas.Title>{t('directory.title')}</Offcanvas.Title>
            </Offcanvas.Header>
            <Offcanvas.Body>
                <Tabs variant="underline" defaultActiveKey="products" className="mb-3 invoice-tab border-bottom flex-wrap">
                    <Tab
                        eventKey="products"
                        title={
                            <>
                                <i className="bi bi-box-seam me-1 fs-14"></i>
                                {t('directory.tabs.products')}
                            </>
                        }
                    >
                        <ProductsServicesKeysTab onKeySelect={onKeySelect} />
                    </Tab>

                    <Tab
                        eventKey="document"
                        title={
                            <>
                                <i className="bi bi-file-earmark-text me-1 fs-14"></i>
                                {t('directory.tabs.document')}
                            </>
                        }
                    >
                        <DocumentKeysTab onKeySelect={onKeySelect} />
                    </Tab>

                    {showSpecification ? (
                        <Tab
                            eventKey="specification"
                            title={
                                <>
                                    <i className="bi bi-card-list me-1 fs-14"></i>
                                    {t('directory.tabs.specification')}
                                </>
                            }
                        >
                            <SpecificationKeysTab objectDelails={objectDelails} onKeySelect={onKeySelect} />
                        </Tab>
                    ) : null}

                    <Tab
                        eventKey="customer"
                        title={
                            <>
                                <i className="bi bi-person-vcard me-1 fs-14"></i>
                                {t('directory.tabs.customer')}
                            </>
                        }
                    >
                        <CustomerKeysTab onKeySelect={onKeySelect} />
                    </Tab>

                    <Tab
                        eventKey="client"
                        title={
                            <>
                                <i className="bi bi-people me-1 fs-14"></i>
                                {t('directory.tabs.client')}
                            </>
                        }
                    >
                        <ClientKeysTab onKeySelect={onKeySelect} />
                    </Tab>

                    {showGeneral ? (
                        <Tab
                            eventKey="general"
                            title={
                                <>
                                    <i className="bi bi-info-circle me-1 fs-14"></i>
                                    {t('directory.tabs.general')}
                                </>
                            }
                        >
                            <GeneralKeysTab onKeySelect={onKeySelect} />
                        </Tab>
                    ) : null}
                </Tabs>
            </Offcanvas.Body>
        </Offcanvas>
    );
};

export default TemplateKeysOffcanvas;
